import { useContext, useEffect, useState } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  Instrument,
  InvestmentTransaction,
  InvestmentTransactionCollection,
} from "wealthmode-frontend/backend"
import { Text } from "wealthmode-frontend/gluestack/ui"
import { formatMoneyFull } from "wealthmode-frontend/util"

import { DetailField, ResourceDetail } from "@/components"
import { BackendContext } from "@/context"

export default function InvestmentTransactionDetail() {
  const { client_id, account_id, investment_transaction_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
    investment_transaction_id: string
  }>()

  const backend = useContext(BackendContext)
  const [transaction, setTransaction] = useState<InvestmentTransaction>()

  useEffect(() => {
    ;(async () => {
      const clients = backend.collection("clients") as ClientCollection
      const client = clients.model(client_id)
      await client.fetch({ reload: true })

      const accounts = client.link("accounts")?.resolve() as AccountCollection
      const account = accounts.model(account_id)
      await account.fetch({ reload: true })

      const data = account.embed("data") as AccountData
      const transactions = data.link("investment_transactions")?.resolve() as
        InvestmentTransactionCollection | undefined
      if (!transactions) {
        return
      }

      const model = transactions.model(investment_transaction_id)
      await model.fetch({ reload: true })

      setTransaction(model)
    })()
  }, [backend, client_id, account_id, investment_transaction_id])

  const fields: DetailField[] = []

  if (transaction) {
    const instrument = (transaction.embed("instrument") as Instrument | null) ?? null

    fields.push(
      { label: "Date", value: String(transaction.attributes.date ?? "") },
      { label: "Category", value: transaction.attributes.category ?? "" },
      { label: "Kind", value: transaction.attributes.kind ?? "" },
      {
        label: "Instrument",
        value:
          instrument == null ? (
            ""
          ) : (
            <Link href={`/instruments/${instrument.attributes.id}`}>
              <Text className="text-markdown-link hover:opacity-80 underline whitespace-nowrap">
                {instrument.attributes.symbol ??
                  (instrument.attributes.type == "cash" ? "Cash" : instrument.attributes.type)}
              </Text>
            </Link>
          ),
      },
      { label: "Price", value: formatMoneyFull(transaction.attributes.price) ?? "" },
      { label: "Quantity", value: String(transaction.attributes.quantity ?? "") },
      { label: "Fees", value: formatMoneyFull(transaction.attributes.fees) ?? "" },
      { label: "Holding Δ", value: String(transaction.attributes.holding_delta ?? "") },
      { label: "Instrument Δ", value: String(transaction.attributes.instrument_delta ?? "") },
      { label: "Cash Δ", value: formatMoneyFull(transaction.attributes.cash_delta) ?? "" },
      { label: "Value Δ", value: formatMoneyFull(transaction.attributes.value_delta) ?? "" },
      { label: "Identities Valid", value: transaction.attributes.identities_valid ? "Yes" : "No" },
    )
  }

  return (
    <ResourceDetail
      title={transaction?.attributes.description || "Investment Transaction"}
      subtitle={investment_transaction_id}
      loading={!transaction}
      fields={fields}
      links={[]}
    />
  )
}
